import { Injectable } from '@angular/core';
import { AbcCore, DataUrl } from '@barthauer/abc/core';
import { CacheStorageAdapter, OnlineService } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ConnectableObservable, fromEvent, Observable, Subject } from 'rxjs';
import { publishReplay } from 'rxjs/operators';
import { PlatformService } from './platform.service';

declare let navigator: any;
declare let Camera: any;
declare let window: any;
declare let cordova: any;
declare let device: any;

@Injectable()
export class CordovaService implements OnlineService {
  public isMobileDeviceReadyDetected: boolean;
  public pictureResolution: number = 1080;
  public pictureQuality: number = 50;
  private readonly isOnlineChangedSubject = new Subject<boolean>();
  private readonly isDeviceReady = new Subject<void>();
  private readonly connectionTypeChangedSubject = new Subject<string>();
  private resAsync: Promise<ResourceSet>;
  private isOnline_: boolean;
  private openWindows: Map<string, any> = new Map();
  private listenersAttached: boolean = false;
  public static get isCordovaEnvironmentDetected(): boolean {
    let isCordovaEnvironmentDetected: boolean = false;
    try {
      if (cordova) {
        isCordovaEnvironmentDetected = true;
      }
    } catch {}
    return isCordovaEnvironmentDetected;
  }

  public get isOnline(): boolean {
    return this.isOnline_;
  }

  private connectionType_: string;
  public get connectionType(): string {
    return this.connectionType_;
  }

  private setIsOnline(value: boolean, connectionType: string) {
    if (this.isOnline != value) {
      this.isOnline_ = value;
      this.isOnlineChangedSubject.next(value);
    }
    if (connectionType != this.connectionType) {
      this.connectionType_ = connectionType;
      this.connectionTypeChangedSubject.next(connectionType);
    }
  }

  public get isOnlineChanged(): Observable<boolean> {
    return this.isOnlineChangedSubject.asObservable();
  }

  public get onDeviceReady(): Observable<void> {
    return this.isDeviceReady.asObservable();
  }

  public get connectionTypeChanged(): Observable<string> {
    return this.connectionTypeChangedSubject.asObservable();
  }

  private getCurrentConnectionType(): string {
    return navigator.connection?.type ?? navigator.connection?.effectiveType;
  }

  private cordovaWindowOpen = (url: string, target?: string, features?: string) => {
    // If it's a named window that already exists, close it first
    if (target) {
      const existingWindow = this.openWindows.get(target);
      if (existingWindow && !existingWindow.closed) {
        existingWindow.close();
      }
    }
    // Open new InAppBrowser window
    const defaultFeatures =
      'location=yes,toolbar=yes,closebuttoncaption=Fertig,hidenavigationbuttons=no,hideurlbar=no,toolbarposition=top,clearcache=no,clearsessioncache=no';
    const inAppBrowserRef = cordova.InAppBrowser.open(url, target || '_blank', features || defaultFeatures);

    // Create a window-like object that mimics standard window behavior
    const windowProxy = {
      closed: false,
      location: { href: url },
      postMessage: (message: any, targetOrigin: string) => {
        // Execute script in InAppBrowser to handle postMessage
        const script = `
          (function() {
            if (window.handleParentMessage) {
              window.handleParentMessage(${JSON.stringify(message)});
            }
          })();
        `;
        inAppBrowserRef.executeScript({ code: script });
      },
      close: () => {
        if (inAppBrowserRef) {
          inAppBrowserRef.close();
          windowProxy.closed = true;
          if (target) {
            this.openWindows.delete(target);
          }
        }
      },
    };

    // Handle InAppBrowser events
    inAppBrowserRef.addEventListener('exit', () => {
      windowProxy.closed = true;
      if (target) {
        this.openWindows.delete(target);
      }
    });

    inAppBrowserRef.addEventListener('loaderror', (event) => {
      console.error('InAppBrowser load error:', event);
    });

    // Store reference if it's a named window
    if (target) {
      this.openWindows.set(target, windowProxy);
    }

    return windowProxy;
  };

  //attention: "this" of the eventhandler is in vs.code-debugger wrong,
  //in chrome not, its "myselfe" like intended
  private handleOnline = () => this.setIsOnline(true, this.getCurrentConnectionType());
  private handleOffline = () => this.setIsOnline(false, this.getCurrentConnectionType());
  constructor(
    resourceService: ResourceManagerService,
    public platform: PlatformService,
    abcCore: AbcCore,
  ) {
    this.resAsync = resourceService.getResourceSet('abc-core-cordova');
    const deviceReady = fromEvent(document, 'deviceready');
    (deviceReady.pipe(publishReplay(1)) as ConnectableObservable<Event>).connect();

    //deviceReady only fires in "cordova mode" -> means app
    deviceReady.subscribe(() => {
      this.isMobileDeviceReadyDetected = true;

      if (platform.isIOSOrIPadOS) {
        window.open = this.cordovaWindowOpen;
      }
      if (platform.isBrowser) {
        window.open = this.cordovaWindowOpen;
        if (!platform.isAndroid) {
        }
      }
      //disable back button
      document.addEventListener('backbutton', (e) => {
        e.preventDefault();
      });

      this.setIsOnline(navigator.onLine || CacheStorageAdapter.isServerAvailable, this.getCurrentConnectionType());
      if (CacheStorageAdapter.onlineService) {
        CacheStorageAdapter.onlineService.removeEventListening();
      }
      CacheStorageAdapter.onlineService = this;
      if (!this.listenersAttached) {
        document.addEventListener('online', this.handleOnline);
        document.addEventListener('offline', this.handleOffline);
        this.listenersAttached = true;
      }

      const conn = navigator.connection;
      if (conn) {
        var connType = navigator.connection.type;
        setInterval(() => {
          var newConnType = navigator.connection.type;
          if (connType !== newConnType) {
            this.setIsOnline(navigator.onLine, this.getCurrentConnectionType());
            connType = newConnType;
          }
        }, 1000);
      }
      this.isDeviceReady.next();
    });
  }
  public removeEventListening(): void {
    if (this.listenersAttached) {
      document.removeEventListener('online', this.handleOnline);
      document.removeEventListener('offline', this.handleOffline);
      this.listenersAttached = false;
    }

    // Close all open windows
    this.openWindows.forEach((windowProxy) => {
      if (!windowProxy.closed) {
        windowProxy.close();
      }
    });
    this.openWindows.clear();
  }

  public takePicture(useGallery?: boolean): Promise<DataUrl> {
    return new Promise((resolve, reject) => {
      const options = {
        quality: this.pictureQuality,
        destinationType: Camera.DestinationType.DATA_URL,
        sourceType: useGallery ? Camera.PictureSourceType.SAVEDPHOTOALBUM : Camera.PictureSourceType.CAMERA,
        allowEdit: false,
        encodingType: Camera.EncodingType.JPEG,
        mediaType: Camera.MediaType.PICTURE,
        correctOrientation: true,
        saveToPhotoAlbum: false,
        targetWidth: this.pictureResolution,
        targetHeight: this.pictureResolution,
      };

      navigator.camera.getPicture(
        (dataUrl) => resolve(DataUrl.fromString(dataUrl)),
        (error) => {
          if (error && !error.toLowerCase().startsWith('no image')) {
            this.resAsync.then((resset) => alert(resset.get('Cordova-Camera-Error', error)));
            reject();
          }
        },
        options,
      );
    });
  }
}
